import { FileUploader } from "react-drag-drop-files";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Progress,
  Stack,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, InfoIcon } from "@chakra-ui/icons";
import FileUpload from "../../components/FileUploadIcon";
import {
  DropDown,
  SecondaryButton,
} from "../../../../components/design_library";
import { Step } from "../../../../components/design_library/Progress/Steps";
import { useStep } from "../../../../components/design_library/Progress/useStep";
import { useAIUploadController } from "./useAIUploadController";
import AIPreviewTable from "./AIPreviewTable";
import { useAtomValue } from "jotai";
import { AccountAtom, TransactionsAtom } from "../../../../store";
import { useTransactions } from "../../../../hooks/useTransactions";
import { useEffect, useRef } from "react";

const aiFileTypes = ["CSV", "XLSX"];

type Props = {
  closeModal: () => void;
  selectedBook: string;
  setSelectedBook: (v: string) => void;
  calculateStrategy: { value: string; label: string };
  setCalculateStrategy: (v: any) => void;
  selectedAccountForUpload: any;
  setSelectedAccountForUpload: (v: any) => void;
  /** When the uploads modal opens, reset wizard to the upload step */
  isModalOpen?: boolean;
};

export default function AIUpload(props: Props) {
  const accountState = useAtomValue(AccountAtom);
  const transactionsState = useAtomValue(TransactionsAtom);
  const transactions = useTransactions();
  const books = transactionsState.books.map((book: any) => ({
    value: book,
    label: book,
  }));

  const [currentStep, stepHelpers] = useStep({ maxStep: 2, initialStep: 0 });

  const controller = useAIUploadController({
    closeModal: props.closeModal,
    selectedBook: props.selectedBook,
    calculateStrategy: props.calculateStrategy,
    selectedAccountForUpload: props.selectedAccountForUpload,
  });

  const {
    previewTrades,
    previewCashflows,
    brokerDetected,
    loading,
    uploading,
    errorMessage,
    previewFile,
    confirmUpload,
    removeTradeRow,
    updateTradeRow,
    resetPreviewState,
    importSuccess,
    successTradesCount,
    successCashflowsCount,
  } = controller;

  const stepResetRef = useRef(stepHelpers.reset);
  stepResetRef.current = stepHelpers.reset;

  const goToNextStepRef = useRef(stepHelpers.goToNextStep);
  goToNextStepRef.current = stepHelpers.goToNextStep;

  const previewAdvanceGuard = useRef(false);
  useEffect(() => {
    if (loading) {
      previewAdvanceGuard.current = false;
    }
  }, [loading]);

  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (
      wasLoadingRef.current &&
      !loading &&
      (previewTrades.length > 0 || previewCashflows.length > 0) &&
      !errorMessage &&
      currentStep === 0 &&
      !previewAdvanceGuard.current
    ) {
      previewAdvanceGuard.current = true;
      goToNextStepRef.current();
    }
    wasLoadingRef.current = loading;
  }, [
    loading,
    previewTrades.length,
    previewCashflows.length,
    errorMessage,
    currentStep,
  ]);

  const prevModalOpen = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (props.isModalOpen === undefined) return;
    if (prevModalOpen.current === false && props.isModalOpen === true) {
      previewAdvanceGuard.current = false;
      stepResetRef.current();
      resetPreviewState();
    }
    if (prevModalOpen.current === true && props.isModalOpen === false) {
      previewAdvanceGuard.current = false;
      stepResetRef.current();
      resetPreviewState();
    }
    prevModalOpen.current = props.isModalOpen;
  }, [props.isModalOpen, resetPreviewState]);

  const lastAccountIdRef = useRef<number | null>(null);
  useEffect(() => {
    const accountId =
      props.selectedAccountForUpload?.id || accountState.selectedAccount?.id;
    if (accountId && accountId !== lastAccountIdRef.current) {
      lastAccountIdRef.current = accountId;
      transactions.getBooks(accountId);
    }
  }, [props.selectedAccountForUpload?.id, accountState.selectedAccount?.id]);

  const accountIdForPreview =
    props.selectedAccountForUpload?.id || accountState.selectedAccount?.id;

  const brokerLabel = brokerDetected?.trim() || "Unknown broker";
  const tradeCount = previewTrades.length;
  const cfCount = previewCashflows.length;

  const handleBackFromConfigure = () => {
    previewAdvanceGuard.current = false;
    resetPreviewState();
    stepHelpers.setStep(0);
  };

  const handleBackFromReview = () => {
    stepHelpers.goToPrevStep();
  };

  return (
    <Box w="100%" maxW="100%" minW={0} minH="480px" overflow="hidden" position="relative">
      {importSuccess && (
        <Flex
          position="absolute"
          inset={0}
          zIndex={10}
          align="center"
          justify="center"
          bg="rgba(0,0,0,0.65)"
          borderRadius="md"
          direction="column"
          gap={3}
          px={4}
        >
          <CheckIcon color="var(--accent)" boxSize={10} />
          <Text fontSize="lg" fontWeight="semibold" textAlign="center">
            Imported {successTradesCount} trade
            {successTradesCount === 1 ? "" : "s"}
            {successCashflowsCount > 0
              ? ` · ${successCashflowsCount} cash flow${
                  successCashflowsCount === 1 ? "" : "s"
                }`
              : ""}
          </Text>
          <Text fontSize="sm" color="gray.400">
            Closing…
          </Text>
        </Flex>
      )}

      <VStack alignItems="flex-start" spacing={4} w="100%" maxW="100%" minW={0}>
        <Stack
          direction={{ base: "column", md: "row" }}
          w="100%"
          spacing={{ base: 2, md: 0 }}
          align={{ base: "stretch", md: "center" }}
        >
          <Flex flex="1" minW={0} wrap="wrap" gap={0}>
            <Step
              title="Upload file"
              description="CSV or XLSX"
              isActive={currentStep === 0}
              isCompleted={currentStep > 0}
              isFirstStep={true}
              isLastStep={false}
            />
            <Step
              title="Configure"
              description="Portfolio & book"
              isActive={currentStep === 1}
              isCompleted={currentStep > 1}
              isFirstStep={false}
              isLastStep={false}
            />
            <Step
              title="Review & import"
              description="Edit & confirm"
              isActive={currentStep === 2}
              isCompleted={false}
              isFirstStep={false}
              isLastStep={true}
            />
          </Flex>
          {(brokerDetected || tradeCount > 0 || cfCount > 0) && (
            <HStack flexShrink={0}>
              {brokerDetected ? (
                <Badge colorScheme="purple" variant="subtle" fontSize="0.7rem">
                  {brokerDetected}
                </Badge>
              ) : null}
            </HStack>
          )}
        </Stack>

        {currentStep === 0 && (
          <>
            <Text fontSize="sm" color="gray.400" maxW="100%">
              Upload a CSV or XLSX export from any broker. We map up to{" "}
              <strong>500 trades</strong> and <strong>500 cash flows</strong> per
              file using AI. Choose the portfolio first, then select your file.
            </Text>

            <VStack alignItems="flex-start" w="100%">
              <HStack>
                <Heading size="xs" fontWeight={400}>
                  Import to portfolio
                </Heading>
                <Tooltip label="Preview and import use this portfolio.">
                  <InfoIcon />
                </Tooltip>
              </HStack>
              <DropDown
                value={{
                  value: props.selectedAccountForUpload?.id || "",
                  label:
                    props.selectedAccountForUpload?.name ||
                    accountState.selectedAccount?.name ||
                    "Select portfolio",
                }}
                onChange={(e: any) => {
                  const acc = accountState.accounts.find(
                    (a: any) => a.id === e.value
                  );
                  props.setSelectedAccountForUpload(acc);
                }}
                options={accountState.accounts.map((account: any) => ({
                  value: account.id,
                  label: account.name,
                }))}
                placeholder="Select portfolio"
              />
            </VStack>

            <Box w="100%" maxW="100%" minW={0} sx={{ "& > *": { maxWidth: "100%" } }}>
              {loading ? (
                <Box
                  w="100%"
                  py={10}
                  px={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="whiteAlpha.200"
                  bg="var(--dark-gray)"
                >
                  <VStack spacing={4}>
                    <Box
                      animation="pulse 2s ease-in-out infinite"
                      sx={{
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.45 },
                        },
                      }}
                    >
                      <FileUpload h="40px" w="40px" />
                    </Box>
                    <Text fontWeight={600}>Analyzing file with AI…</Text>
                    <Progress
                      w="100%"
                      maxW="320px"
                      size="sm"
                      borderRadius="full"
                      isIndeterminate
                      colorScheme="blue"
                      sx={{
                        "& > div": { bg: "var(--accent)" },
                      }}
                    />
                  </VStack>
                </Box>
              ) : (
                <FileUploader
                  handleChange={(file: File) => previewFile(file)}
                  name="file"
                  types={aiFileTypes}
                  disabled={loading}
                  disableDragAndDrop={true}
                >
                  <Box
                    w="100%"
                    maxW="100%"
                    minW={0}
                    boxSizing="border-box"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDir="column"
                    className="drop-zone ai-upload-drop-zone"
                    background="var(--dark-gray)"
                    border="4px dashed var(--gray)"
                    borderRadius="8px"
                    py="48px"
                    cursor="pointer"
                    mb="10px"
                    transition="box-shadow 0.2s ease, border-color 0.2s ease"
                    _hover={{
                      borderColor: "var(--accent)",
                      boxShadow: "0 0 0 1px var(--accent), 0 0 24px rgba(59, 130, 246, 0.25)",
                    }}
                  >
                    <FileUpload h="40px" w="40px" />
                    <HStack spacing={2} mt="2" justify="center" flexWrap="wrap">
                      <Text fontWeight={600}>AI Upload</Text>
                      <Badge
                        colorScheme="purple"
                        fontSize="0.65rem"
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                        borderRadius="sm"
                      >
                        Alpha
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" textAlign="center" px={4} mt={1}>
                      Click to upload (CSV · XLSX)
                    </Text>
                  </Box>
                </FileUploader>
              )}
            </Box>

            {errorMessage ? (
              <Alert status="error" borderRadius="md" w="100%">
                <AlertIcon />
                {errorMessage}
              </Alert>
            ) : null}
          </>
        )}

        {currentStep === 1 && (
          <>
            <Box w="100%">
              <Text fontSize="sm" color="gray.300" mb={4}>
                <strong>
                  {tradeCount} trade{tradeCount === 1 ? "" : "s"}
                </strong>
                {" and "}
                <strong>
                  {cfCount} cash flow{cfCount === 1 ? "" : "s"}
                </strong>
                {" detected"}
                {brokerDetected ? (
                  <>
                    {" from "}
                    <strong>{brokerLabel}</strong>
                  </>
                ) : null}
                .
              </Text>

              <HStack w="full" flexWrap="wrap" gap={4} alignItems="flex-start">
                <VStack alignItems="flex-start">
                  <HStack>
                    <Heading size="xs" fontWeight={400}>
                      Calculate strategy
                    </Heading>
                    <Tooltip label="Automatically calculate strategy from uploaded trades.">
                      <InfoIcon />
                    </Tooltip>
                  </HStack>
                  <DropDown
                    value={{
                      label: props.calculateStrategy.label,
                      value: props.calculateStrategy.value,
                    }}
                    onChange={(e: any) => props.setCalculateStrategy(e)}
                    options={[
                      { value: "NONE", label: "Do not generate" },
                      { value: "STRATEGY", label: "Strategy field" },
                      { value: "POSITION_GROUP", label: "Position Group field" },
                      { value: "BOOK", label: "Book field" },
                    ]}
                    placeholder="Selected strategy field"
                  />
                </VStack>

                <VStack alignItems="flex-start">
                  <HStack>
                    <Heading size="xs" fontWeight={400}>
                      Assign trades to book
                    </Heading>
                    <Tooltip label="Optional book assignment for imported trades.">
                      <InfoIcon />
                    </Tooltip>
                  </HStack>
                  <DropDown
                    value={{
                      value: props.selectedBook,
                      label:
                        props.selectedBook === "undefined" ||
                        props.selectedBook === ""
                          ? "No book selected"
                          : props.selectedBook,
                    }}
                    onChange={(e: any) => props.setSelectedBook(e.value)}
                    options={[
                      { value: "undefined", label: "No book selected" },
                      ...books,
                    ]}
                    placeholder="No book selected"
                  />
                </VStack>
              </HStack>
            </Box>

            <HStack w="full" justify="space-between" pt={2} flexWrap="wrap" gap={3}>
              <SecondaryButton type="button" onClick={handleBackFromConfigure}>
                Back — choose another file
              </SecondaryButton>
              <Button
                type="button"
                bg="var(--accent)"
                color="var(--bg)"
                _hover={{ filter: "brightness(1.05)" }}
                onClick={() => stepHelpers.goToNextStep()}
              >
                Next — review & import
              </Button>
            </HStack>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Divider />
            <Heading size="sm" w="full">
              Preview — confirm or edit
            </Heading>
            <Text fontSize="sm" color="gray.400">
              <strong>
                {tradeCount} trade{tradeCount === 1 ? "" : "s"}
              </strong>
              {cfCount > 0 ? (
                <>
                  {", "}
                  <strong>
                    {cfCount} cash flow{cfCount === 1 ? "" : "s"}
                  </strong>
                </>
              ) : null}
              {brokerDetected ? (
                <>
                  {" · "}
                  {brokerLabel}
                </>
              ) : null}
            </Text>

            {errorMessage ? (
              <Alert status="error" borderRadius="md" w="100%">
                <AlertIcon />
                {errorMessage}
              </Alert>
            ) : null}

            <AIPreviewTable
              trades={previewTrades}
              cashflows={previewCashflows}
              accountId={accountIdForPreview}
              readOnly={false}
              onRemoveRow={removeTradeRow}
              onCellChange={(index, field, value) => {
                updateTradeRow(index, field, value);
              }}
            />
            <HStack
              w="full"
              justifyContent="space-between"
              pt={4}
              flexWrap="wrap"
              gap={3}
              borderTopWidth="1px"
              borderColor="whiteAlpha.200"
            >
              <SecondaryButton type="button" onClick={handleBackFromReview}>
                Back — configure
              </SecondaryButton>
              <Button
                type="button"
                onClick={() => confirmUpload()}
                isLoading={uploading}
                loadingText="Importing…"
                isDisabled={
                  previewTrades.length === 0 && previewCashflows.length === 0
                }
                size="lg"
                h="48px"
                px={8}
                fontWeight="semibold"
                fontSize="md"
                borderRadius="md"
                leftIcon={<CheckIcon boxSize={5} />}
                w={{ base: "full", md: "auto" }}
                minW={{ md: "280px" }}
                bg="var(--accent)"
                color="var(--bg)"
                _hover={{ filter: "brightness(1.05)" }}
                _active={{ filter: "brightness(0.95)" }}
                _disabled={{ opacity: 0.45, cursor: "not-allowed" }}
                aria-label={`Confirm import: ${previewTrades.length} trades, ${previewCashflows.length} cash flows`}
              >
                Confirm import · {previewTrades.length} trade
                {previewTrades.length === 1 ? "" : "s"}
                {previewCashflows.length > 0
                  ? ` · ${previewCashflows.length} cash flow${
                      previewCashflows.length === 1 ? "" : "s"
                    }`
                  : ""}
              </Button>
            </HStack>
          </>
        )}
      </VStack>
    </Box>
  );
}
